import DOMPurify from "dompurify";
import axiosInstance from "../../../common/infrastructure/helpers/axiosHelper";
import { IAnnouncement } from "../models/announcementModel";

/**
 * The editor stores images without the API origin, so one announcement works in every
 * environment: `<img src="/announcement-images/{guid}.gif">`. Only a src that starts exactly
 * like this is expanded, so an absolute URL (already expanded, or an outside image) is never
 * touched a second time.
 */
const STORED_IMAGE_SRC = /\bsrc\s*=\s*(["'])\/announcement-images\//gi;

/** The API origin and path images are served from: the axios base URL, without a trailing slash. */
export const getAnnouncementApiBase = (): string =>
  (axiosInstance.defaults.baseURL ?? "").replace(/\/+$/, "");

/** Prefixes the stored image paths with the API base, for both quote styles. */
export const expandAnnouncementImageUrls = (
  html: string,
  apiBase: string = getAnnouncementApiBase(),
): string => {
  const base = apiBase.replace(/\/+$/, "");
  return html.replace(
    STORED_IMAGE_SRC,
    (_match, quote: string) => `src=${quote}${base}/announcement-images/`,
  );
};

// Rich text only: nothing that runs code, loads a page into the dialog, restyles the whole
// app (<style> is allowed by DOMPurify's defaults) or posts a form. Event handlers and
// javascript: links are already removed by the defaults.
const FORBIDDEN_TAGS = [
  "script",
  "iframe",
  "object",
  "embed",
  "style",
  "form",
  "input",
  "textarea",
  "select",
  "button",
];

type Purifier = ReturnType<typeof DOMPurify>;

let purifier: Purifier | null = null;

/**
 * A DOMPurify instance of our own, so the link hook below never leaks into any other use of
 * the shared default instance. Created on first use, when `window` exists.
 */
const getPurifier = (): Purifier => {
  if (purifier) {
    return purifier;
  }

  const instance = DOMPurify(window);
  // Links open in a new tab and cannot reach back into the app through window.opener.
  instance.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "A") {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer");
    }
  });

  purifier = instance;
  return instance;
};

/** Removes everything unsafe from the HTML; the result is safe for dangerouslySetInnerHTML. */
export const sanitizeAnnouncementHtml = (html: string): string => {
  const instance = getPurifier();
  // Fail closed where DOMPurify cannot run: no content is better than unchecked content.
  if (!instance.isSupported) {
    return "";
  }

  return instance.sanitize(html, { FORBID_TAGS: FORBIDDEN_TAGS });
};

/** The HTML to render: image paths expanded to the API origin first, then sanitized. */
export const prepareAnnouncementHtml = (
  html: string,
  apiBase: string = getAnnouncementApiBase(),
): string => sanitizeAnnouncementHtml(expandAnnouncementImageUrls(html ?? "", apiBase));

/** Keeps only well-formed announcements from the response, in the order the server sent them. */
export const normalizeAnnouncements = (data: unknown): IAnnouncement[] => {
  if (!Array.isArray(data)) {
    return [];
  }

  const announcements: IAnnouncement[] = [];
  data.forEach((item) => {
    if (!item || typeof item !== "object") {
      return;
    }

    const candidate = item as Partial<IAnnouncement>;
    if (typeof candidate.id !== "number" || !Number.isFinite(candidate.id)) {
      return;
    }

    announcements.push({
      id: candidate.id,
      title: typeof candidate.title === "string" ? candidate.title : "",
      contentHtml: typeof candidate.contentHtml === "string" ? candidate.contentHtml : "",
      createdOn: typeof candidate.createdOn === "string" ? candidate.createdOn : "",
    });
  });

  return announcements;
};
